import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const SentenceBuilderGame = ({ gameData, onComplete }) => {
    const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
    const [userSentence, setUserSentence] = useState([]);
    const [availableWords, setAvailableWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [showGrammarTip, setShowGrammarTip] = useState(false);

    useEffect(() => {
        if (gameData?.sentences?.[currentSentenceIndex]) {
            const sentence = gameData.sentences[currentSentenceIndex];
            setAvailableWords(sentence.shuffled_words || []);
            setUserSentence([]);
            setFeedback(null);
            setShowHint(false);
            setShowGrammarTip(false);
        }
    }, [currentSentenceIndex, gameData]);

    const currentSentence = gameData?.sentences?.[currentSentenceIndex];

    const addWord = (word, index) => {
        if (feedback) return;
        triggerHaptic(30);
        setUserSentence(prev => [...prev, word]);
        setAvailableWords(prev => prev.filter((_, i) => i !== index));
    };

    const removeWord = (index) => {
        if (feedback) return;
        triggerHaptic(20);
        const word = userSentence[index];
        setUserSentence(prev => prev.filter((_, i) => i !== index));
        setAvailableWords(prev => [...prev, word]);
    };

    const clearSentence = () => {
        if (feedback) return;
        triggerHaptic(40);
        setAvailableWords(prev => [...prev, ...userSentence]);
        setUserSentence([]);
    };

    const checkAnswer = () => {
        if (feedback || userSentence.length === 0) return;
        const normalize = (s) => s.replace(/[.!?]+$/, '').trim();
        const userAnswer = normalize(userSentence.join(' '));
        const correctAnswer = normalize(currentSentence.correct_sentence);

        if (userAnswer === correctAnswer) {
            setFeedback('correct');
            const pointsToAdd = gameData.points_per_sentence || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentSentenceIndex + 1 >= gameData.sentences.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentSentenceIndex(prev => prev + 1);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                clearSentence();
            }, 2500);
        }
    };

    if (!gameData || !gameData.sentences || !currentSentence) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-9xl mb-8">✍️</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Konstruojami sakiniai...</h3>
            </div>
        );
    }

    const progress = (currentSentenceIndex / gameData.sentences.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:p-6">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-100 rounded-2xl md:rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-purple-200 animate-float">
                        ✍️
                    </div>
                    <div>
                        <h3 className="text-base md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                            Sakinių <span className="text-purple-600">Konstruktorius</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 md:mb-2">Iškalba</p>
                        <p className="text-sm md:text-3xl font-black text-purple-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[80px] md:min-w-[120px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 md:mb-2">Eiga</p>
                        <p className="text-sm md:text-3xl font-black text-slate-800 leading-none">{currentSentenceIndex + 1}/{gameData.sentences.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-slate-200/50 rounded-full h-2 md:h-4 p-0.5 md:p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8">
            {/* Visual Card */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="text-6xl md:text-[120px] drop-shadow-[0_40px_80px_rgba(168,85,247,0.4)] animate-float mb-6 md:mb-12">{currentSentence.emoji}</div>
                <div className="bg-white/80 rounded-xl md:rounded-[3rem] p-4 md:p-8 shadow-2xl border-4 border-purple-50 relative group px-6 md:px-12">
                    <p className="text-sm md:text-2xl font-bold text-slate-800 italic font-serif leading-relaxed">
                        "{gameData.instructions || 'Sujunk žodžius!'}"
                    </p>
                </div>
                
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                    <button
                        onClick={() => { triggerHaptic(20); setShowHint(!showHint); }}
                        className={`px-4 py-2 md:px-8 md:py-4 rounded-xl font-black text-[10px] md:text-xs transition-all border-b-4 uppercase ${showHint ? 'bg-amber-500 text-white border-amber-700' : 'bg-white text-amber-600 border-amber-100 hover:bg-amber-50'}`}
                    >
                        💡 UŽUOMINA
                    </button>
                    <button
                        onClick={() => { triggerHaptic(20); setShowGrammarTip(!showGrammarTip); }}
                        className={`px-4 py-2 md:px-8 md:py-4 rounded-xl font-black text-[10px] md:text-xs transition-all border-b-4 uppercase ${showGrammarTip ? 'bg-indigo-500 text-white border-indigo-700' : 'bg-white text-indigo-600 border-indigo-100 hover:bg-indigo-50'}`}
                    >
                        📚 TAISYKLĖ
                    </button>
                </div>

                {(showHint || showGrammarTip) && (
                    <div className="mt-4 space-y-2 w-full animate-pop-in">
                        {showHint && <div className="bg-amber-50 p-4 rounded-xl border-2 border-amber-100 text-xs md:text-sm italic">"{currentSentence.hint}"</div>}
                        {showGrammarTip && <div className="bg-indigo-50 p-4 rounded-xl border-2 border-indigo-100 text-xs md:text-sm font-bold">{currentSentence.grammar_tip}</div>}
                    </div>
                )}
            </div>

            {/* Word Bank */}
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-8 shadow-2xl border-white/60 bg-white/60 backdrop-blur-md text-center">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.5em] mb-4 md:mb-8">Prieinami Žodžiai</h4>
                <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
                    {availableWords.map((word, index) => (
                        <button
                            key={index}
                            onClick={() => addWord(word, index)}
                            disabled={!!feedback}
                            className="premium-button-3d px-4 py-3 md:px-10 md:py-6 bg-white text-slate-900 rounded-xl md:rounded-[2rem] font-black text-sm md:text-2xl shadow-md border-b-[6px] md:border-b-[10px] border-slate-100 hover:border-purple-400 transition-all active:translate-y-1 active:border-b-0 disabled:opacity-30"
                        >
                            {word}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-3 md:space-y-6">
            <div className={`
                bg-slate-900/80 rounded-2xl md:rounded-[3rem] p-4 md:p-8 min-h-[120px] md:min-h-[180px] flex flex-wrap gap-2 justify-center items-center border-4 border-dashed transition-all duration-500 shadow-inner
                ${userSentence.length > 0 ? 'border-purple-500/30' : 'border-slate-700'}
            `}>
                {userSentence.length === 0 ? (
                    <div className="text-slate-600 font-black text-sm md:text-2xl italic animate-pulse">Spausk žodžius banke... 👆</div>
                ) : (
                    userSentence.map((word, index) => (
                        <button
                            key={index}
                            onClick={() => removeWord(index)}
                            className="premium-button-3d px-4 py-3 md:px-8 md:py-5 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-xl shadow-lg border-b-[6px] border-indigo-900 active:translate-y-1 active:border-b-0 transition-all"
                        >
                            {word}
                        </button>
                    ))
                )}
            </div>

            <div className="flex gap-2 justify-center">
                <button
                    onClick={clearSentence}
                    disabled={userSentence.length === 0 || !!feedback}
                    className="flex-1 py-3 md:py-6 bg-slate-800 text-slate-400 rounded-xl md:rounded-2xl font-black text-xs md:text-xl border-b-[6px] border-slate-900 active:translate-y-1 active:border-b-0 disabled:opacity-20 uppercase tracking-widest"
                >
                    VALYTI 🔄
                </button>
                <button
                    onClick={checkAnswer}
                    disabled={userSentence.length === 0 || !!feedback}
                    className={`
                        flex-[2] py-3 md:py-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-xl shadow-lg transition-all border-b-[6px] border-emerald-800 uppercase tracking-widest
                        ${userSentence.length > 0 && !feedback ? 'active:translate-y-1 active:border-b-0' : 'opacity-20'}
                    `}
                >
                    PATVIRTINTI ✓
                </button>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[3rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '✍️' : '📖'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-3xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'Tobulas Sakinys!' : 'Netaisyklinga!'}
                            </h2>
                            <div className={`p-4 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-lg font-bold text-slate-700 leading-relaxed">
                                    {feedback === 'correct' ? 'Puiku! Tavo gramatikos įgūdžiai yra stulbinantys!' : 'Oi! Pabandyk žodžius sukeisti vietomis dar kartą!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SentenceBuilderGame;
